import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import allPollens from '@/common/components/controls/targeting/pollen/PollenTargetingEnumerations';
import allUVs from '@/common/components/controls/targeting/uv/UVTargetingEnumerations';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import BudgetModes from '@/common/enums/budget-modes.enum';
import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
import { QueryParams } from '@/common/models';
import { IEstimatedReachTargetParams } from '@/common/models/audience/ReachStatistics';
import CPMCalculator from '@/common/models/CPMCalculator';
import { daypartToRanges } from '@/common/models/Dayparting';
import CuratedPackageDeliveryStatistics, {
  CuratedPackageDeliveryStatisticsEntry,
  ICuratedPackageDeliveryStatisticsAPI,
} from '@/common/models/deliveryStatistics/CuratedDeliveryStatistics';
import EntityFee, { IEntityFeeAPI } from '@/common/models/EntityFee';
import GeofenceGroup, { IGeofenceGroupAPI } from '@/common/models/geo/GeofenceGroup';
import Location, { ILocationAPI } from '@/common/models/geo/Location';
import Market, { IMarketAPI } from '@/common/models/geo/Market';
import { ICoreTargetingAPI, IEstimatedDeliveryStatsAPI } from '@/common/models/InventoryTargeting';
import LineItemPropertyKeys from '@/common/models/lineitem/LineItemPropertyKeys.enum';
import MediaOwner from '@/common/models/media-owner/MediaOwner';
import MediaType, { IMediaTypeAPI } from '@/common/models/misc-api-models/MediaType';
import Network, { INetworkAPI } from '@/common/models/misc-api-models/Network';
import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
import { IPackageDiscoveryParams } from '@/common/models/package/DealDiscovery';
import { getEntityProperties } from '@/common/models/package/Package.entityProperties';
import PackageDeliveryStats from '@/common/models/package/PackageDeliveryStats';
import PackageRestrictions from '@/common/models/package/PackageRestrictions.enum';
import SpendSteering, {
  ISpendSteeringAPI,
  MINIMUM_STEERING_PERCENTAGE,
  SelectableDimensionType,
} from '@/common/models/package/SpendSteering';
import PurchaseTypeOptions from '@/common/models/PurchaseTypeOptions';
import FilterCollection from '@/common/models/query-params/FilterCollection';
import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
import Site, { ISiteAPI } from '@/common/models/site/Site';
import { NetworkFrequencyCap } from '@/common/models/targeting-criteria/NetworkFrequencyCap';
import Unit, { IUnitAPI } from '@/common/models/unit/Unit';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { capitalizeTranslation, DASH, formatCurrency, formatNumber, FullDaypart, toServerDate } from '@/globals';
import { useOrganizationDSPs } from '@/org-app/services/tanstack/queries';
import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
import { getCachedData, peekQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import rootState from '@/store/state';
import { getDiff } from '@/utils/Diff';
import NumberHelper from '@/utils/NumberHelper';
import { mapOrgAppState } from '@/utils/StateMapping';
import Validatable, { IEntityChange, IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import { AuctionType } from './AuctionType.enum';
import { CurationType } from './CurationType.enum';
import PackageCustomProperty from './PackageCustomProperty';
import PackageFeeType from './PackageFeeType.enum';
import PackageProperty, { IPackagePropertyApi } from './PackageProperty';
import PackageStatus from './PackageStatus.enum';
import PackageTargetingCriterion, { IPackageTargetingCriterionApi } from './PackageTargetingCriterion';
import PackageType from './PackageType.enum';

const type = 'Package';
export const DEFAULT_SUSTAINABILITY_THRESHOLD = 80; // https://hivestack.atlassian.net/browse/HSSP-2010
export const EVERGREEN_DATE = '2042-07-20T00:00:00.000Z';
const isCuratorOrganization = () => orgAppStore.getters.isCuratorOrganization(orgAppStore.state);
const createdByUserList = () => {
  const items = peekQueryInScope(useUserOrganizations);
  return items;
};
const organizationExternalExchanges = () => {
  return orgAppStore.modules.ReferenceData.state.organizationExternalExchanges || [];
};
const organizationDSPs = () => {
  const items = peekQueryInScope(useOrganizationDSPs);
  return items;
};
const isDemandOrganization = () => orgAppStore.getters.isDemandOrganization(orgAppStore.state);

export interface IPackageAPI {
  ad_duration_max?: number;
  ad_duration_min?: number;
  ad_duration_overridable?: boolean;
  auction_type?: AuctionType;
  budget_max: number;
  budget_min: number;
  budget_mode: BudgetModes;
  budget_overridable: boolean;
  buyer_external_exchange_ids: number[];
  buyer_external_ids: number[];
  buyer_organization_dsp_ids: number[];
  curator_organization_ids: number[];
  cadence_max?: number;
  cadence_min?: number;
  cadence_smooth_catchup?: boolean;
  creative_formats: string[];
  creative_format_exclusions: string[];
  cpm_fixed: number;
  cpm_net?: number;
  created_by: number;
  created_on_utc?: string;
  cpm_from_rate_card: boolean;
  daypart: string;
  daypart_overridable: boolean;
  deal_id: string;
  demographic_ids: number[];
  direct_billing: boolean;
  exchange_id: number;
  flight_from: string;
  flight_to: string;
  for_bidding?: boolean;
  geofencegroup_ids: number[];
  geofencegroups?: IGeofenceGroupAPI[];
  hide_unit_information_in_bid_request: boolean;
  inventory_targeting: IPackageTargetingAPI;
  id?: number;
  is_evergreen: boolean;
  is_external: boolean;
  is_open?: boolean;
  location_exclusion_ids: number[];
  location_exclusions?: ILocationAPI[];
  location_ids: number[];
  locations?: ILocationAPI[];
  market_ids: number[];
  market_targeted_impressions_max: number;
  market_targeted_impressions_min: number;
  market_targeted_impressions_overridable: boolean;
  markets?: IMarketAPI[];
  mediaowner_ids: number[];
  mediatype_ids: number[];
  mediatypes?: IMediaTypeAPI[];
  min_concentration_factor: number;
  min_concentration_value: number;
  min_seconds_between_plays_max?: number;
  min_seconds_between_plays_min?: number;
  min_seconds_between_plays_overridable: boolean;
  modified_on_utc?: string;
  name: string;
  network_ids: number[];
  networks?: INetworkAPI[];
  notes: string;
  number_of_days_max?: number;
  number_of_days_min?: number;
  number_of_days_overridable?: boolean;
  organization_id: number;
  package_children?: IPackageAPI[];
  package_demographictargets_overridable: boolean;
  package_eventtargetingcriteria_overridable: boolean;
  package_fee?: { type: string; value: number };
  entity_fees: IEntityFeeAPI[];
  package_id: number;
  package_ids?: number[];
  package_locationtargets_overridable: boolean;
  package_mediatypes_overridable: boolean;
  package_networks_overridable: boolean;
  package_properties: IPackagePropertyApi[];
  package_targetingcriteria: IPackageTargetingCriterionApi[];
  package_targetingcriteria_overridable: boolean;
  package_type?: PackageType;
  curation_type?: CurationType;
  package_unitassignments_overridable: boolean;
  plan_id: number;
  purchase_type: PurchaseTypesEnum;
  purchase_type_overridable: boolean;
  revised_by_package_id?: number;
  revision: number;
  site_exclusion_ids: number[];
  site_exclusions?: ISiteAPI[];
  site_ids: number[];
  sites: ISiteAPI[];
  state: PackageStatus;
  unit_exclusion_ids: number[];
  unit_exclusions?: IUnitAPI[];
  unit_tags: string[];
  unit_ids: number[];
  unitpack_exclusion_ids: number[];
  unitpack_ids: number[];
  units: IUnitAPI[];
  unit_tag_exclusions: string[];
  estimated_delivery_statistics?: ICuratedPackageDeliveryStatisticsAPI;
  spend_steering?: ISpendSteeringAPI;
  currency?: string;
}

interface IPackageTargetParams {
  ad_duration: number;
  creative_format_exclusions: string[];
  creative_formats: string[];
  creative_orientations: CreativeOrientationType[];
  creative_orientation_exclusions: CreativeOrientationType[];
  iab_categories: string[];
  iab_category_exclusions: string[];
  daypart: string;
  demographic_ids: number[];
  from_date: string;
  geofencegroup_exclusion_ids: number[];
  geofencegroup_ids: number[];
  lineitem_id: number;
  location_exclusion_ids: number[];
  location_ids: number[];
  market_ids: number[];
  media_type_ids: number[];
  network_ids: number[];
  owner_ids: number[];
  package_id: number;
  purchase_type: PurchaseTypesEnum;
  screen_frequency_cap: number;
  site_exclusion_ids: number[];
  site_ids: number[];
  to_date: string;
  unit_exclusion_ids: number[];
  unit_ids: number[];
  unit_pack_exclusion_ids: number[];
  unit_pack_ids: number[];
  unit_tag_exclusions: string[];
  unit_tags: string[];
}

interface IPackageCpmTargetParams extends IPackageTargetParams {
  buyer_organization_dsp_id: number;
  buyer_organization_id: number;
  buyer_exchange_id: number;
  lineitem_id: number;
  max_bid_cpm: number;
  measurement_requested: boolean;
  min_concentration_factor: number;
  min_concentration_value: number;
  owner_organization_ids: number[];
  package_exchange_id: number;
  package_organization_id: number;
  screen_frequency_cap: number;
  unit_exclusion_ids: number[];
  unit_ids: number[];
  unitpack_exclusion_ids: number[];
  unitpack_ids: number[];
  creative_formats: string[];
  creative_format_exclusions: string[];
  unit_tags: string[];
  unit_tag_exclusions: string[];
}

type IPackageTargetingAPI = ICoreTargetingAPI;

interface ICO2Emissions {
  adSelectionGco2e: number;
  creativeDeliveryGco2e: number;
  inventoryTargetingID: number;
  mediaDistributionGco2e: number;
  score: number;
  totalGco2e: number;
}

export default class Package extends Validatable<number> {
  static type: string = 'Package';
  static isGettingMarkets = false;
  static marketsCache: Market[] = [];
  static isGettingMediaTypes = false;
  static mediaTypesCache: MediaType[] = [];
  static isGettingUnitPacks = false;
  static unitPacksCache: MediaType[] = [];

  adDurationMax: number;
  adDurationMin: number;
  adDurationOverridable: boolean;
  apiObject: IPackageAPI;
  approvalComment: string;
  auctionType: AuctionType;
  biddingMetrics: {
    bidRequestInTheLastHour: boolean;
    bidResponseInTheLastHour: boolean;
    lastPlayedWithinInterval: boolean;
  };
  budgetMax: number;
  budgetMin: number;
  budgetMode: BudgetModes;
  budgetOverridable: boolean;
  buyerExternalExchangeIDs: number[];
  buyerExternalIDs: number[];
  /**
   * The deal Buyer Organization DSPs. Loaded from the `buyerOrganizationDSPIDs` property.
   * Can be loaded by using the action `orgApp/getPackageBuyerDSPs` or the action `orgApp/getPackage` with the `loadBuyerDSPs` parameter to `true`
   */
  buyerOrganizationDSPs: OrganizationDSP[];
  buyerOrganizationDSPIDs: number[];
  curatorOrganizationIDs: number[];
  cachedOriginalObject: Package;
  cadenceMax: number;
  cadenceMin: number;
  cadenceSmoothCatchup: boolean;
  cpmFixed: number;
  cpmNet: number;
  cpmCalculator: CPMCalculator;
  createdBy: number;
  createdOnUtc: string;
  creativeFormatExclusions: string[];
  creativeFormats: string[];
  creativeOrientations: CreativeOrientationType[];
  creativeOrientationExclusions: CreativeOrientationType[];
  iabCategories: string[];
  iabCategoryExclusions: string[];
  co2Emissions: ICO2Emissions;
  co2EmissionsScore: number;
  co2EmissionsScoreThreshold: number;
  customEventTargetingOverridable: boolean;
  daypart: string;
  daypartOverridable: boolean;
  dealID: string;
  demographicTargetIDs: number[];
  demographicTargetsOverridable: boolean;
  directBilling: boolean;
  endDate: DateTime;
  estimatedAudienceCPM: number | null;
  estimatedAverageAudienceCPM: number | null;
  estimatedAverageCPM: number | null;
  estimatedCPM: number | null;
  exchangeID: number;
  entityFees: EntityFee[];
  geofenceGroupExclusionIDs: number[];
  geofenceGroupIDs: number[];
  geofenceGroups: GeofenceGroup[];
  hideUnitInformationInBidRequests: boolean;
  isCPMFromRateCard: boolean;
  isEvergreen: boolean;
  isExternal: boolean;
  isOpen: boolean;
  isForBidding: boolean;
  isInlineEdited: boolean;
  lineitemID: number;
  languageExclusionIDs: number[];
  languageIDs: number[];
  locationExclusionIDs: number[];
  locationExclusions: Location[];
  locationTargetIDs: number[];
  locationTargetsOverridable: boolean;
  locations: Location[];
  marketExclusionIDs: number[];
  marketIDs: number[];
  marketTargetedImpressionsMax: number;
  marketTargetedImpressionsMin: number;
  marketTargetedImpressionsOverridable: boolean;
  markets: Market[];
  maxBidCPM: number;
  MaxXandrDurationInDays: number;
  mediaOwner: MediaOwner;
  mediaOwnerIDs: number[];
  mediaTypeExclusionIDs: number[];
  mediaTypeIDs: number[];
  mediaTypes: MediaType[];
  mediaTypesOverridable: boolean;
  minConcentrationFactor: number;
  minConcentrationValue: number;
  minSecondsBetweenPlaysMax: number;
  minSecondsBetweenPlaysMin: number;
  minSecondsBetweenPlaysOverridable: boolean;
  modifiedOnUtc: string;
  name: string;
  networkExclusionIDs: number[];
  networkFrequencyCaps: NetworkFrequencyCap[];
  networkIDs: number[];
  networks: Network[];
  networksOverridable: boolean;
  notes: string;
  numberOfDaysMax: number;
  numberOfDaysMin: number;
  numberOfDaysOverridable: boolean;
  organizationID: number;
  packageIDs: number[];
  packageCustomProperties: PackageCustomProperty[];
  packageProperties: PackageProperty[];
  packages: Package[];
  packageTargetingCriteria: PackageTargetingCriterion[];
  packageTargetingCriteriaOverridable: boolean;
  packageType: PackageType;
  planID: number;
  purchaseType: PurchaseTypesEnum;
  purchaseTypeOverridable: boolean;
  revisedByID: number;
  revision: number;
  siteExclusionIDs: number[];
  siteExclusions: Site[];
  siteIDs: number[];
  sites: Site[];
  spendSteering: SpendSteering;
  startDate: DateTime;
  state: PackageStatus;
  timestamp?: DateTime;
  type: string;
  units: Unit[];
  unitAssignmentsOverridable: boolean;
  unitExclusionIDs: number[];
  unitExclusions: Unit[];
  unitIDs: number[];
  unitPackExclusionIDs: number[];
  unitPackIDs: number[];
  unitTagExclusions: string[];
  unitTags: string[];

  deliveryStats?: PackageDeliveryStats;
  curatedDeliveryStatistics?: CuratedPackageDeliveryStatistics | null;
  curationType?: CurationType | null;
  // Will only be populated for curated deals, it's not mutable
  currency?: string;

  constructor(
    id = -1,
    name = '',
    state = PackageStatus.Draft,
    notes = null,
    flightFrom = DateTime.local().plus({ days: 7 }).startOf('week'),
    flightTo = DateTime.local().plus({ days: 14 }).endOf('week'),
    numberOfDaysMin = null,
    numberOfDaysMax = null,
    numberOfDaysOverridable = null,
    adDurationMin = 10,
    adDurationMax = 60,
    adDurationOverridable = false,
    minSecondsBetweenPlaysMin = 0,
    minSecondsBetweenPlaysMax = null,
    minSecondsBetweenPlaysOverridable = false,
    budgetMode = BudgetModes.BUDGET,
    budgetMin = 0,
    budgetMax = 0,
    budgetOverridable = false,
    cpmFixed = null,
    cpmNet = null,
    isCPMFromRateCard = null,
    /**
     * `true` is for Stroer Deal (deal created from Stroer platform)
     * `false` means it's PG HS Deal
     */
    isExternal = false,
    marketTargetedImpressionsMin = null,
    marketTargetedImpressionsMax = null,
    marketTargetedImpressionsOverridable = false,
    demographicTargetIDs = [],
    demographicTargetsOverridable = false,
    locationTargetIDs = [],
    locationExclusionIDs = [],
    locations = [],
    locationTargetsOverridable = false,
    unitAssignmentsOverridable = false,
    networkIDs = [],
    networks = [],
    networksOverridable = false,
    mediaTypeIDs = [],
    mediaTypes = [],
    mediaTypesOverridable = false,
    siteIDs = [],
    siteExclusionIDs = [],
    sites = [],
    unitIDs = [],
    unitExclusionIDs = [],
    units = [],
    unitPackExclusionIDs = [],
    unitPackIDs = [],
    geofenceGroupIDs = [],
    geofenceGroupExclusionIDs = [],
    marketIDs = [],
    daypartOverridable = null,
    daypart = FullDaypart,
    buyerExternalExchangeIDs = [],
    buyerOrganizationDSPIDs = [],
    curatorOrganizationIDs = [],
    dealID = null,
    purchaseType = null,
    purchaseTypeOverridable = false,
    organizationID = null,
    revision = null,
    packageProperties = [],
    packageTargetingCriteria = [],
    packageTargetingCriteriaOverridable = false,
    minConcentrationFactor = 1,
    isForBidding = false,
    exchangeID = null,
    revisedByID = null,
    createdOnUtc = null,
    modifiedOnUtc = null,
    mediaOwnerIDs = [],
    markets = [],
    geofenceGroups = [],
    locationExclusions = [],
    siteExclusions = [],
    unitExclusions = [],
    customEventTargetingOverridable = false,
    buyerExternalIDs = [],
    unitTags = [],
    unitTagExclusions = [],
    hideUnitInformationInBidRequests = false,
    cadenceMin = null,
    cadenceMax = null,
    cadenceSmoothCatchup = false,
    creativeFormats = [],
    creativeFormatExclusions = [],
    auctionType = AuctionType.FirstPrice,
    isOpen = false,
    directBilling = false,
    isEvergreen = false,
    planID = null,
    packageType = PackageType.REGULAR,
    biddingMetrics = null,
    co2Emissions: ICO2Emissions = null,
    co2EmissionsScore = null,
    co2EmissionsScoreThreshold = null,
    packages = null,
    entityFees = [],
    packageIDs = undefined,
    languageIDs = [],
    languageExclusionIDs = [],
    creativeOrientations = [],
    creativeOrientationExclusions = [],
    iabCategories = [],
    iabCategoryExclusions = [],
    timestamp = DateTime.local(),
    minConcentrationValue = 0,
    curatedDeliveryStatistics = null,
    curationType = null,
    spendSteering = null,
    currency = null,
  ) {
    super();

    this.type = type;
    this.id = id;
    this.name = name;
    this.state = state;
    this.notes = notes;
    this.startDate = flightFrom;
    this.endDate = flightTo;
    this.numberOfDaysMin = numberOfDaysMin;
    this.numberOfDaysMax = numberOfDaysMax;
    this.numberOfDaysOverridable = numberOfDaysOverridable;
    this.adDurationMin = adDurationMin;
    this.adDurationMax = adDurationMax;
    this.adDurationOverridable = adDurationOverridable;
    this.minSecondsBetweenPlaysMin = minSecondsBetweenPlaysMin;
    this.minSecondsBetweenPlaysMax = minSecondsBetweenPlaysMax;
    this.minSecondsBetweenPlaysOverridable = minSecondsBetweenPlaysOverridable;
    this.budgetMode = budgetMode;
    this.budgetMin = budgetMin;
    this.budgetMax = budgetMax;
    this.budgetOverridable = budgetOverridable;
    this.cpmFixed = cpmFixed;
    this.cpmNet = cpmNet;
    this.creativeFormats = creativeFormats;
    this.creativeFormatExclusions = creativeFormatExclusions;
    this.creativeOrientations = creativeOrientations;
    this.creativeOrientationExclusions = creativeOrientationExclusions;
    this.iabCategories = iabCategories;
    this.iabCategoryExclusions = iabCategoryExclusions;
    this.isCPMFromRateCard = isCPMFromRateCard;
    this.isExternal = isExternal;
    this.marketTargetedImpressionsMin = marketTargetedImpressionsMin;
    this.marketTargetedImpressionsMax = marketTargetedImpressionsMax;
    this.marketTargetedImpressionsOverridable = marketTargetedImpressionsOverridable;
    this.demographicTargetIDs = demographicTargetIDs;
    this.demographicTargetsOverridable = demographicTargetsOverridable;
    this.languageIDs = languageIDs;
    this.languageExclusionIDs = languageExclusionIDs;
    this.locationTargetIDs = locationTargetIDs;
    this.locationExclusionIDs = locationExclusionIDs;
    this.locations = locations;
    this.locationExclusions = locationExclusions;
    this.locationTargetsOverridable = locationTargetsOverridable;
    this.unitAssignmentsOverridable = unitAssignmentsOverridable;
    this.networkIDs = networkIDs;
    this.networks = networks;
    this.networksOverridable = networksOverridable;
    this.mediaTypeIDs = mediaTypeIDs;
    this.mediaTypes = mediaTypes;
    this.mediaTypesOverridable = mediaTypesOverridable;
    this.siteIDs = siteIDs;
    this.siteExclusionIDs = siteExclusionIDs;
    this.sites = sites;
    this.siteExclusions = siteExclusions;
    this.unitIDs = unitIDs;
    this.unitExclusionIDs = unitExclusionIDs;
    this.units = units;
    this.unitExclusions = unitExclusions;
    this.unitPackExclusionIDs = unitPackExclusionIDs;
    this.unitPackIDs = unitPackIDs;
    this.geofenceGroupIDs = geofenceGroupIDs;
    this.geofenceGroupExclusionIDs = geofenceGroupExclusionIDs;
    this.marketIDs = marketIDs;
    this.daypartOverridable = daypartOverridable;
    this.daypart = daypart;
    this.buyerExternalExchangeIDs = buyerExternalExchangeIDs;
    this.buyerOrganizationDSPIDs = buyerOrganizationDSPIDs;
    this.curatorOrganizationIDs = curatorOrganizationIDs;
    this.dealID = dealID;
    this.purchaseType = purchaseType;
    this.purchaseTypeOverridable = purchaseTypeOverridable;
    this.organizationID = organizationID;
    this.revision = revision;
    this.packageProperties = packageProperties;
    this.packageTargetingCriteria = packageTargetingCriteria;
    this.packageTargetingCriteriaOverridable = packageTargetingCriteriaOverridable;
    this.minConcentrationFactor = minConcentrationFactor;
    this.minConcentrationValue = minConcentrationValue;
    this.isForBidding = isForBidding;
    this.exchangeID = exchangeID;
    this.revisedByID = revisedByID;
    this.mediaOwnerIDs = mediaOwnerIDs;
    this.markets = markets;
    this.geofenceGroups = geofenceGroups;
    this.hideUnitInformationInBidRequests = hideUnitInformationInBidRequests;
    this.lineitemID = -1;
    this.estimatedAudienceCPM = null;
    this.estimatedCPM = null;
    this.estimatedAverageAudienceCPM = null;
    this.estimatedAverageCPM = null;
    this.cpmCalculator = new CPMCalculator(this, null);
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.customEventTargetingOverridable = customEventTargetingOverridable;
    this.buyerExternalIDs = buyerExternalIDs;
    this.unitTagExclusions = unitTagExclusions;
    this.unitTags = unitTags;
    this.cadenceMin = cadenceMin;
    this.cadenceMax = cadenceMax;
    this.cadenceSmoothCatchup = cadenceSmoothCatchup;
    this.auctionType = auctionType;
    this.isOpen = isOpen;
    this.directBilling = directBilling;
    this.biddingMetrics = biddingMetrics;
    this.planID = planID;
    this.isEvergreen = isEvergreen;
    this.packageType = packageType;
    this.packages = packages;
    this.co2Emissions = co2Emissions;
    this.co2EmissionsScore = co2EmissionsScore;
    this.co2EmissionsScoreThreshold = co2EmissionsScoreThreshold;
    this.entityFees = entityFees;
    this.packageIDs = packageIDs;
    this.timestamp = timestamp;
    this.spendSteering = spendSteering;
    this.currency = currency;

    this.isInlineEdited = false;
    this.loadCustomProperties();

    this.MaxXandrDurationInDays = 365;
    this.curatedDeliveryStatistics = curatedDeliveryStatistics;
    this.curationType = curationType;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Package.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Package.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Package.entityPropertiesCached = null;
    });
  }

  get adDurationDescription(): string {
    if (
      (this.adDurationMin == null || this.adDurationMin === 0) &&
      (this.adDurationMax == null || this.adDurationMax === 0)
    ) {
      return i18n.global.t('messages.basedOnScreensAdDuration');
    }

    return this.adDurationMin === this.adDurationMax
      ? `${this.adDurationMin} ${i18n.global.t('names.second', { n: 2 })}`
      : `${this.adDurationMin} — ${this.adDurationMax} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get adDurationRequirements(): string {
    if (this.adDurationOverridable) {
      return '';
    }

    if (this.adDurationMin === this.adDurationMax) {
      return `${i18n.global.t('states.fixed')}: ${this.adDurationMin}`;
    }

    return this.adDurationMin !== null
      ? `${i18n.global.t('names.min', { n: 1 })}: ${this.adDurationMin}, ${i18n.global.t('names.max', { n: 1 })}: ${this.adDurationMax}`
      : `${i18n.global.t('names.max', { n: 1 })}: ${this.adDurationMax}`;
  }

  get areImpressionsFixed(): boolean {
    return (
      this.marketTargetedImpressionsMin !== null &&
      this.marketTargetedImpressionsMin === this.marketTargetedImpressionsMax
    );
  }

  // TODO We need to establish whether or not to add curator restrictions to external flag
  get auctionTypeFormatted(): string {
    if (this.isNGExternalOrPreferredExternalDeal || this.curatorOrganizationIDs?.length > 0) {
      if (this.auctionType === AuctionType.FixedPrice) {
        return capitalize(i18n.global.t('states.fixedPrice'));
      }
      if (this.auctionType === AuctionType.FirstPrice) {
        return capitalize(i18n.global.t('states.firstPrice'));
      }
    }
    return DASH;
  }

  get bidResponseInTheLastHour(): boolean {
    return this.biddingMetrics?.bidResponseInTheLastHour;
  }

  get bidResponseInTheLastHourDescription(): string {
    const key = this.bidResponseInTheLastHour ? 'yes' : 'no';
    return capitalize(i18n.global.t(`actions.${key}`));
  }

  get bidRequestSentInTheLastHour(): boolean {
    return this.biddingMetrics?.bidRequestInTheLastHour;
  }

  get bidRequestSentInTheLastHourDescription(): string {
    const key = this.bidRequestSentInTheLastHour ? 'yes' : 'no';
    return capitalize(i18n.global.t(`actions.${key}`));
  }

  get budgetDescription(): string {
    if (this.isPreferredOrNGInternalDeal) {
      return DASH;
    }

    if (this.goal === 'impressions') {
      if (this.isCPMFromRateCard) {
        return DASH;
      }

      let min = null;
      let max = null;
      if (this.cpmNet != null) {
        min = Math.round((this.marketTargetedImpressionsMin / 1000) * this.cpmCalculator.cpmNetMin);
        max = Math.round((this.marketTargetedImpressionsMax / 1000) * this.cpmCalculator.cpmNetMax);
      } else if (this.cpmFixed != null && this.cpmFixed > 0) {
        min = Math.round((this.marketTargetedImpressionsMin / 1000) * this.cpmCalculator.cpmFixed);
        max = Math.round((this.marketTargetedImpressionsMax / 1000) * this.cpmCalculator.cpmFixed);
      }

      if (min != null && max != null) {
        if (min !== max) {
          return `${formatNumber(min, '$0a')} — ${formatNumber(max, '$0a')}`;
        }
        return formatNumber(min, '$0a');
      }

      return DASH;
    }

    if (this.isBudgetFixed) {
      if (this.budgetMin === null) {
        return DASH;
      }
      return this.minBudgetFormattedShort;
    }
    return `${this.minBudgetFormattedShort} — ${this.maxBudgetFormattedShort}`;
  }

  get buyEndpoint(): string {
    if (this.isCuratedDeal) {
      return rootStore.getters.hasChosenOrganization
        ? `/lineitems/new/edit/curated-package/${this.id}`
        : `/exch/lineitems/new/edit/curated-package/${this.id}`;
    } else {
      return rootStore.getters.hasChosenOrganization
        ? `/lineitems/new/edit/package/${this.id}`
        : `/exch/lineitems/new/edit/package/${this.id}`;
    }
  }

  get countries(): string {
    if (isCuratorOrganization() && this.isCuratedDeal) {
      const orgIDs = this.packages.map((x) => x.organizationID);
      const countries = rootState.exchApp.suppliers
        .filter((x) => orgIDs.includes(x.id))
        .map((x) => x.countries)
        .flat();
      return countries.length ? [...new Set(countries)].join(', ') : DASH;
    }
    const org = rootState.exchApp.suppliers.find((x) => x.id === this.organizationID);
    return org?.countries?.join(', ') ?? DASH;
  }

  get mediaOwners() {
    // We don't need to use scope on queryClient.getQueryData
    let items = getCachedData([queryKeys.mediaOwners]);
    const ownerIDs = [...new Set(this.ownerIDsFromChildDeals)];
    if (!items) {
      // In case we have them cached in the details view for performance purposes.
      const qp = QueryParams.all(
        FilterCollection.for('organization_id', ownerIDs, FilterComparisonOperator.IN),
      ).toQueryString();
      items = getCachedData([queryKeys.mediaOwners, qp]);
    }
    if (Array.isArray(items)) {
      return items.filter((x) => (this.isCuratedDeal ? ownerIDs?.includes(x.id) : x.id === this.organizationID));
    }
    return [];
  }

  get currencyInformation(): { currency: string; symbol: string } {
    const ownerIDs = this.ownerIDsFromChildDeals;
    const id = this.isCuratedDeal && ownerIDs?.length > 0 ? ownerIDs[0] : this.organizationID;
    const organization = this.mediaOwners.find((supplier: MediaOwner) => supplier.id === id);
    return {
      currency: this.currency || organization?.currency,
      symbol: organization?.currencySymbol,
    };
  }

  get cpmDescription(): string {
    const currency = this.currencyInformation.symbol || '$';
    if (isCuratorOrganization() && !this.isCPMFromRateCard) {
      return `${currency}${this.cpmCalculator.cpmDescription.replace(/[a-zA-Z$£¥€]+/g, '')}`;
    }
    return this.cpmCalculator.cpmDescription;
  }

  get reachTargetParams(): IEstimatedReachTargetParams {
    return {
      ...this.targetParams,
      apply_realtime_multiplier: true,
      impression_goal: this.cpmCalculator.estimatedTargetImpressions,
    };
  }

  get cpmTargetParams(): IPackageCpmTargetParams {
    return {
      ...this.targetParams,
      buyer_organization_dsp_id: null,
      buyer_organization_id: this.organizationID,
      buyer_exchange_id: this.exchangeID,
      lineitem_id: -1,
      max_bid_cpm: null,
      measurement_requested: false,
      min_concentration_factor: this.minConcentrationFactor,
      min_concentration_value: this.minConcentrationValue,
      owner_organization_ids: this.targetParams.owner_ids,
      package_exchange_id: null,
      package_organization_id: null,
      screen_frequency_cap: 0,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      unitpack_ids: this.unitPackIDs,
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
    };
  }

  get dealDiscoveryParams(): IPackageDiscoveryParams {
    return {
      allowed_buyer_access: [PackageRestrictions.OPEN, PackageRestrictions.CURATOR],
      currency: this.currencyInformation.currency,
      flight_from: this.startDate.toFormat('yyyy-MM-dd'),
      flight_to: this.endDate.toFormat('yyyy-MM-dd'),
      for_bidding: this.isForBidding,
      inventory_targeting: {
        // Commented out for the time being until the field is made nullable in the backend.
        // co2e_emissions_score_threshold: this.co2EmissionsScoreThreshold,
        creative_format_exclusions: this.creativeFormatExclusions,
        creative_formats: this.creativeFormats,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        language_exclusion_ids: this.languageExclusionIDs,
        language_ids: this.languageIDs,
        location_exclusion_ids: this.locationExclusionIDs,
        location_ids: this.locationIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        market_ids: this.marketIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        network_ids: this.networkIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        site_ids: this.siteIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        unit_ids: this.unitIDs,
        unit_tag_exclusions: this.unitTagExclusions,
        unit_tags: this.unitTags,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        unitpack_ids: this.unitPackIDs,
      },
    };
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null
      ? DateTime.fromISO(this.createdOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : DASH;
  }

  get datesDescription(): string {
    return this.isEvergreen
      ? `${this.startDate.toLocaleString(DateTime.DATE_SHORT)} — ${capitalize(i18n.global.t('messages.alwaysOn'))}`
      : `${this.startDate.toLocaleString(DateTime.DATE_SHORT)} — ${this.endDate.toLocaleString(DateTime.DATE_SHORT)}`;
  }

  get dealIDFormatted(): string {
    return this.dealID === null ? DASH : `${this.dealID}`;
  }

  get demographicIDs(): number[] {
    return this.demographicTargetIDs;
  }

  get demographics(): string {
    return this.demographicTargetIDs.length > 0
      ? this.demographicTargetIDs.join(', ')
      : i18n.global.t('names.all', { n: 1 });
  }

  get didPlayedInTheLastHour(): boolean {
    return this.biddingMetrics?.lastPlayedWithinInterval;
  }

  get didPlayedInTheLastHourDescription(): string {
    const key = this.didPlayedInTheLastHour ? 'yes' : 'no';
    return capitalize(i18n.global.t(`actions.${key}`));
  }

  get directBillingDescription(): string {
    const key = this.directBilling ? 'yes' : 'no';
    return capitalize(i18n.global.t(`actions.${key}`));
  }

  get editEndpoint(): string {
    return rootStore.getters.hasChosenOrganization
      ? isCuratorOrganization()
        ? `/curate/deals/${this.id}/edit`
        : `/deals/${this.id}/edit`
      : `/exch/deals/${this.id}/edit`;
  }

  get endDateDescription(): string {
    return this.maxEndDateFormatted;
  }

  get endpoint(): string {
    if (rootStore.getters.hasChosenOrganization) {
      return this.isCuratedDeal && (isCuratorOrganization() || isDemandOrganization())
        ? `/curate/deals/${this.id}`
        : `/deals/${this.id}`;
    }
    return `/exch/deals/${this.id}`;
  }

  get customFields() {
    const customFields = rootStore.getters['orgApp/organizationDealCustomFields'];
    return this.packageProperties.filter((x) => customFields.some((cf) => cf.key === x.key));
  }

  get daypartRanges() {
    return daypartToRanges(this.daypart);
  }

  get spendSteeringBudgetMode() {
    if (!this.spendSteering) return null;
    return capitalizeTranslation(
      this.spendSteering.budgetMode === BudgetModes.BUDGET ? 'names.budget' : 'validatorNames.impressions',
    );
  }

  get spendSteeringDimensionType() {
    if (!this.spendSteering) return null;
    return capitalizeTranslation(
      this.spendSteering.dimensionType === SelectableDimensionType.DEAL ? 'names.deal' : 'names.mediaOwner',
    );
  }

  get spendSteeringDimensions(): string[] {
    if (!this.spendSteering) return [];

    const isDealDimensionSelected = this.spendSteering.dimensionType === SelectableDimensionType.DEAL;
    const dimensionMap = isDealDimensionSelected ? this.packages : this.mediaOwners;

    return this.spendSteering.spendSteeringDimensions
      .filter(({ dimensionValue }) => dimensionValue !== '*')
      .map(({ dimensionValue }) => {
        const dimension = dimensionMap.find(
          (item) => String(item[isDealDimensionSelected ? 'dealID' : 'id']) === dimensionValue,
        );
        return dimension?.[isDealDimensionSelected ? 'dealID' : 'name'] ?? dimensionValue;
      });
  }

  // Validates if there are child PDs that are incompatible with each other. Typically, will only be relevant if a PD is modified after the CD has been created.
  get hasMismatchedPublisherDeals(): boolean {
    if (!this.isCuratedDeal || !this.packages?.length) return false;
    let prevForBidding = null;
    return this.packages.some((pack) => {
      if (pack.isForBidding == null) return false;
      if (prevForBidding == null) {
        prevForBidding = pack.isForBidding;
        return false;
      }
      return prevForBidding !== pack.isForBidding;
    });
  }

  get hasIncompatibleRestrictions(): boolean {
    if (!this.isCuratedDeal || !this.packages?.length) return false;
    return this.packages.some((pack) => pack.isForBidding != null && pack.isForBidding !== this.isForBidding);
  }

  static initialize() {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Package.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Package.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Package.entityPropertiesCached = null;
    });
  }

  static get entityProperties() {
    if (Package.entityPropertiesCached != null) {
      return Package.entityPropertiesCached;
    }

    Package.entityPropertiesCached = getEntityProperties();

    return Package.entityPropertiesCached;
  }

  static get expandedProperties(): string[] {
    return [
      'package_targetingcriteria',
      'package_targetingcriteria_overridable',
      'unitpack_ids',
      'unitpack_exclusion_ids',
      'creative_formats',
      'creative_format_exclusions',
      'inventory_targeting',
      'inventory_targeting.co2e_emissions.score',
    ];
  }

  static get expands(): string[] {
    return [
      'package_properties',
      'package_targetingcriteria/packagetargetingcriterion_values',
      'package_unitpacks',
      'package_unitpackexclusions',
      'package_creativeformats',
      'package_creativeformatexclusions',
      'package_external_exchange_restrictions',
      'inventory_targeting.co2e_emissions.score',
      'package_organization_restrictions',
      'package_curator_restrictions',
      'demographic_ids',
      ...(isCuratorOrganization()
        ? [
            'package_children.inventory_targeting.co2e_emissions.score',
            'package_fee',
            'entity_fees',
            'spend_steering.spend_steering_dimensions',
          ]
        : []),
    ];
    //
    // UPDATE EXPANDEDPROPERTIES IF YOU ADD NEW EXPAND QUERY PARAMETER
    //
  }

  get entityFee() {
    if (this.isCuratedDeal && !this.entityFees?.length) {
      return null;
    }
    return this.entityFees.length > 0 ? this.entityFees[0] : new EntityFee();
  }

  get feeType() {
    return this.entityFee?.feeType;
  }

  get feeValue() {
    return this.entityFee?.value;
  }

  get feeAttachableCategoryID() {
    return this.entityFee?.attachableFeeCategoryID;
  }

  get feeTypeFormatted(): string {
    switch (this.feeType) {
      case PackageFeeType.FIXED:
        return i18n.global.t('states.fixed');
      case PackageFeeType.PERCENT:
        return i18n.global.t('states.percent');
      default:
        return DASH;
    }
  }

  get createdByUser(): string {
    const user = createdByUserList().find((x) => x.userID === this.createdBy);
    return user?.email || DASH;
  }

  get formattedDSPName(): string {
    if (organizationExternalExchanges()?.length === 0 && organizationDSPs()?.length === 0) {
      return DASH;
    } else if (this.buyerOrganizationDSPIDs?.length === 0 && this.buyerExternalExchangeIDs?.length === 0) {
      return capitalize(i18n.global.t(`names.all`));
    } else if (this.buyerExternalExchangeIDs?.length > 0) {
      const exchanges = this.buyerExternalExchangeIDs.map((id) => {
        return organizationExternalExchanges().find((exchange) => exchange.externalExchangeID === id)?.name || [];
      });
      return exchanges.join(', ');
    } else if (this.buyerOrganizationDSPIDs?.length > 0) {
      const buyers = this.buyerOrganizationDSPIDs.map((id) => {
        return organizationDSPs().find((buyer) => buyer.id === id)?.organization?.name || [];
      });
      return buyers.join(', ');
    }
    return DASH;
  }

  get feeValueFormatted(): string {
    switch (this.feeType) {
      case PackageFeeType.FIXED:
        return formatCurrency(this.feeValue, { currencyCode: this.currencyInformation.currency });
      case PackageFeeType.PERCENT:
        return formatNumber(this.feeValue, '%0,0.00');
      default:
        return `${this.feeValue}`;
    }
  }

  get goal(): string {
    if (this.budgetMode === BudgetModes.IMPRESSIONS) {
      return 'impressions';
    }
    if (this.budgetMode === BudgetModes.BUDGET) {
      return 'budget';
    }
    return null;
  }

  get hasCustomProperties(): boolean {
    return (
      this.packageCustomProperties?.length > 0 &&
      this.packageProperties.filter((x) => this.packageCustomProperties.find((y) => y.key === x.key) != null).length > 0
    );
  }

  get hasEnded(): boolean {
    return DateTime.local().startOf('day') > this.endDate.startOf('day');
  }

  get hasInventoryTargeting(): boolean {
    return !(
      this.marketIDs?.length === 0 &&
      this.marketExclusionIDs?.length === 0 &&
      this.locationIDs?.length === 0 &&
      this.locationExclusionIDs?.length === 0 &&
      this.mediaTypeIDs?.length === 0 &&
      this.mediaTypeExclusionIDs?.length === 0 &&
      this.networkIDs?.length === 0 &&
      this.networkExclusionIDs?.length === 0 &&
      this.unitIDs?.length === 0 &&
      this.unitExclusionIDs?.length === 0 &&
      this.unitPackIDs?.length === 0 &&
      this.unitPackExclusionIDs?.length === 0 &&
      this.siteIDs?.length === 0 &&
      this.siteExclusionIDs?.length === 0 &&
      this.geofenceGroupIDs?.length === 0 &&
      this.geofenceGroupExclusionIDs?.length === 0 &&
      this.unitTags?.length === 0 &&
      this.creativeFormats?.length === 0 &&
      this.creativeFormatExclusions?.length === 0 &&
      this.creativeOrientations?.length === 0 &&
      this.creativeOrientationExclusions?.length === 0 &&
      this.iabCategories?.length === 0 &&
      this.iabCategoryExclusions?.length === 0
    );
  }

  get hasStarted(): boolean {
    const today = DateTime.local().startOf('day');
    return today >= this.startDate;
  }

  get impressionsDescription(): string {
    if (this.isPreferredOrNGInternalDeal) {
      return DASH;
    }

    if (this.goal === 'budget') {
      if (this.isCPMFromRateCard) {
        return DASH;
      }

      let min = null;
      let max = null;
      if (NumberHelper.isSafeDivider(this.cpmNet)) {
        min = Math.round((this.budgetMin / this.cpmCalculator.cpmNetMax) * 1000);
        max = Math.round((this.budgetMax / this.cpmCalculator.cpmNetMin) * 1000);
      } else if (this.cpmFixed != null && this.cpmFixed > 0) {
        min = Math.round((this.budgetMin / this.cpmCalculator.cpmFixed) * 1000);
        max = Math.round((this.budgetMax / this.cpmCalculator.cpmFixed) * 1000);
      } else {
        return DASH;
      }

      if (min != null && max != null) {
        if (min !== max) {
          return `${formatNumber(min, '0a')} — ${formatNumber(max, '0a')}`;
        }
        if (formatNumber(min, '0a').length === 2) {
          return formatNumber(min, '0.0a');
        }
        return formatNumber(min, '0a');
      }
      return DASH;
    }

    if (this.areImpressionsFixed) {
      if (this.marketTargetedImpressionsMax === null) {
        return DASH;
      }
      return this.minImpressionsFormattedShort;
    }
    return `${this.minImpressionsFormattedShort} — ${this.maxImpressionsFormattedShort}`;
  }

  get isBiddingMetricsVisible(): boolean {
    if ((this.isPGDeal || this.hasCadencePurchaseType) && this.buyerOrganizationDSPIDs?.length > 0) {
      const orgDSPs = rootState?.orgApp?.ReferenceData?.organizationDSPs;
      const org = orgDSPs.find((x) => x.id === this.buyerOrganizationDSPIDs[0]);
      return org?.dsp?.isExternal;
    }
    return this.isNGExternalOrPreferredExternalDeal;
  }

  get isBudgetFixed(): boolean {
    return this.budgetMin === this.budgetMax;
  }

  get hasCadencePurchaseType(): boolean {
    return this.purchaseType === PurchaseTypesEnum.CADENCE;
  }

  get isCuratedDeal(): boolean {
    return this.packageType === PackageType.CURATED;
  }

  get isDaypartEmpty(): boolean {
    return this.daypart === null || this.daypart?.indexOf('1') === -1;
  }

  get isDaypartEmptyOrFull(): boolean {
    return this.isDaypartEmpty || this.isDaypartFull;
  }

  get isDaypartFull(): boolean {
    return this.daypart !== null && this.daypart?.indexOf('0') === -1;
  }

  get isExternalDeal(): boolean {
    return this.buyerOrganizationDSPs?.length > 0 && this.buyerOrganizationDSPs.every((org) => org.dsp.isExternal);
  }

  get isFlexible(): boolean {
    // Don't look if min and max values are not equal since they can be equal if the deal scope is buyer definable.
    return (
      this.daypartOverridable ||
      this.adDurationOverridable ||
      this.minSecondsBetweenPlaysOverridable ||
      this.mediaTypesOverridable ||
      this.networksOverridable ||
      this.locationTargetsOverridable ||
      this.demographicTargetsOverridable ||
      this.budgetOverridable ||
      this.marketTargetedImpressionsOverridable
    );
  }

  get isLive(): boolean {
    const today = DateTime.local().startOf('day');
    return this.startDate <= today && this.endDate >= today;
  }

  get isMobileRetargetingEnabled(): boolean {
    let enabled = false;
    this.packageProperties.forEach((x) => {
      if (x.key === 'mobile_retargeting' && x.value === 'true') {
        enabled = true;
      }
    });
    return enabled;
  }

  get isNGExternalOrPreferredExternalDeal(): boolean {
    return (
      (this.purchaseType === PurchaseTypesEnum.PREFERRED || this.purchaseType === PurchaseTypesEnum.NON_GUARANTEED) &&
      this.isForBidding
    );
  }

  get isNGDeal(): boolean {
    return this.purchaseType === PurchaseTypesEnum.NON_GUARANTEED;
  }

  get isNGExternalDeal(): boolean {
    // Other DSP Buyers
    return this.purchaseType === PurchaseTypesEnum.NON_GUARANTEED && this.isForBidding;
  }

  get isNGInternalDeal(): boolean {
    // Hivestack DSP buyers
    // HSSP-2973: Modified so that isForBidding should be false directly, since null would indicate curator restrictions.
    return this.purchaseType === PurchaseTypesEnum.NON_GUARANTEED && this.isForBidding === false;
  }

  get isNGOrPreferredDeal(): boolean {
    return this.isPreferredDeal || this.isNGDeal;
  }

  get isPGDeal(): boolean {
    return this.purchaseType === PurchaseTypesEnum.GUARANTEED;
  }

  get isPGExternalDeal(): boolean {
    return this.isPGDeal && this.isExternalDeal;
  }

  // Checks if deal is a predefined PG deal (CAM-2562)
  get isPredefinedPGDeal(): boolean {
    return (
      this.purchaseType === PurchaseTypesEnum.GUARANTEED && // Is a PG deal
      !this.isFlexible
    ); // Is predefined
  }

  get isPreferredDeal(): boolean {
    return this.purchaseType === PurchaseTypesEnum.PREFERRED;
  }

  get isPreferredExternalDeal(): boolean {
    // Other DSP Buyers
    return this.purchaseType === PurchaseTypesEnum.PREFERRED && this.isForBidding;
  }

  get isPreferredInternalDeal(): boolean {
    // Hivestack DSP buyers
    return this.purchaseType === PurchaseTypesEnum.PREFERRED && !this.isForBidding;
  }

  get isPreferredOrNGInternalDeal(): boolean {
    return (
      (this.purchaseType === PurchaseTypesEnum.PREFERRED || this.purchaseType === PurchaseTypesEnum.NON_GUARANTEED) &&
      !this.isForBidding
    );
  }

  get isPrivate(): boolean {
    return this.buyerOrganizationDSPIDs?.length === 1;
  }

  get isRetired(): boolean {
    return this.state === PackageStatus.Retired;
  }

  get locationIDs(): number[] {
    return this.locationTargetIDs;
  }

  get marketExclusionIDsIncludingChildren(): number[] {
    return this.packages != null
      ? [
          ...(this.marketExclusionIDs || []),
          ...Array.from(new Set(this.packages.map((x) => x.marketExclusionIDs).flat())),
        ]
      : this.marketExclusionIDs;
  }

  get marketIDsIncludingChildren(): number[] {
    return this.packages != null
      ? [...(this.marketIDs || []), ...Array.from(new Set(this.packages.map((x) => x.marketIDs).flat()))]
      : this.marketIDs;
  }

  get maxBudgetFormatted(): string {
    return i18n.global.n(this.budgetMax, 'currency');
  }

  get maxBudgetFormattedShort(): string {
    return formatNumber(this.budgetMax, '$0a');
  }

  get maxEndDateFormatted(): string {
    return this.isEvergreen || EVERGREEN_DATE == this.endDate.toUTC().startOf('day').toString()
      ? DASH
      : this.endDate.toLocaleString(DateTime.DATE_SHORT);
  }

  get maxImpressionsFormatted(): string {
    return formatNumber(this.marketTargetedImpressionsMax, '0,0');
  }

  get maxImpressionsFormattedShort(): string {
    const format = formatNumber(this.marketTargetedImpressionsMax, '0a').length === 2 ? '0.0a' : '0a';
    return formatNumber(this.marketTargetedImpressionsMax, format);
  }

  get mediaTypesDescription(): string {
    return this.mediaTypes.length > 0
      ? this.mediaTypes.map((x) => x.name).join(', ')
      : i18n.global.t('messages.allMediaTypes');
  }

  get minBudgetFormatted(): string {
    return i18n.global.n(this.budgetMin, 'currency');
  }

  get minBudgetFormattedShort(): string {
    return formatNumber(this.budgetMin, '$0a');
  }

  static get minExpands(): string[] {
    return ['package_properties', 'package_demographictargets', 'inventory_targeting'];
  }

  get minImpressionsFormatted(): string {
    return formatNumber(this.marketTargetedImpressionsMin, '0,0');
  }

  get minImpressionsFormattedShort(): string {
    const format = formatNumber(this.marketTargetedImpressionsMin, '0a').length === 2 ? '0.0a' : '0a';
    return formatNumber(this.marketTargetedImpressionsMin, format);
  }

  get minSecondsBetweenPlaysDescription(): string {
    return this.minSecondsBetweenPlaysMin === this.minSecondsBetweenPlaysMax
      ? `${this.minSecondsBetweenPlaysMin}`
      : `${this.minSecondsBetweenPlaysMin} — ${this.minSecondsBetweenPlaysMax}`;
  }

  get minSecondsBetweenPlaysRequirements(): string {
    if (this.minSecondsBetweenPlaysOverridable) {
      return '';
    }

    if (this.minSecondsBetweenPlaysMin === this.minSecondsBetweenPlaysMax) {
      return `${i18n.global.t('states.fixed')}: ${this.minSecondsBetweenPlaysMin}`;
    }

    return this.minSecondsBetweenPlaysMin !== null
      ? `${i18n.global.t('names.min', { n: 1 })}: ${this.minSecondsBetweenPlaysMin}, ${i18n.global.t('names.max', { n: 1 })}: ${this.minSecondsBetweenPlaysMax}`
      : `${i18n.global.t('names.max', { n: 1 })}: ${this.minSecondsBetweenPlaysMax}`;
  }

  get minStartDateFormatted(): string {
    return this.startDate.toLocaleString(DateTime.DATE_SHORT);
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null
      ? DateTime.fromISO(this.modifiedOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : DASH;
  }

  get networksDescription(): string {
    return this.networks.length > 0
      ? this.networks.map((x) => x.name).join(', ')
      : i18n.global.t('messages.allNetworks');
  }

  get originalObject(): Package {
    if (!this.apiObject) return null;

    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Package.fromApi(this.apiObject);
    }

    return this.cachedOriginalObject;
  }

  async getChanges(): Promise<IEntityChange<Package>[]> {
    if (this.isNew) return [];

    const exclusions = [
      ...(this.isEvergreen ? ['endDate'] : []),
      ...(this.isCuratedDeal
        ? ['daypart', 'mediaOwner']
        : ['spendSteeringBudgetMode', 'spendSteeringDimensionType', 'spendSteeringDimensions']),
    ];
    return super.getValidatableChanges<Package>(this.originalObject, Package.entityProperties, exclusions);
  }

  get ownerIDsFromChildDeals(): number[] {
    return this.packages?.flatMap((x) => x.mediaOwnerIDs);
  }

  get ownerIDsFromChildDealsWrapped(): { id: number }[] {
    return this.ownerIDsFromChildDeals?.map((x) => ({
      id: x,
    }));
  }

  get planIDFormatted(): string {
    return this.planID ? `${this.planID}` : DASH;
  }

  get properties(): PackageProperty[] {
    return this.packageProperties;
  }

  get purchaseTypeName(): string {
    const { purchaseType } = this;
    const option = PurchaseTypeOptions.options(true, true).filter((x) => x.value === purchaseType)[0];
    return option != null ? option.name : DASH;
  }

  get siteCountFormatted(): string {
    return formatNumber(this.siteIDs.length, '0,0');
  }

  get startDateDescription(): string {
    return this.minStartDateFormatted;
  }

  /**
   * When buying a deal, we need to set the start date to today if the deal is starting in the past
   * This is needed because we don't want a LI starting in the past + it screws the EDS calculation for CPM
   *
   * @returns DateTime
   */
  get startDateWhenBuyingFromDeal(): DateTime {
    const today = DateTime.local();
    const defaultStartDate = today.plus({ days: 7 });

    if (this.startDate == null) {
      return defaultStartDate;
    }

    const isDealStartingInThePast = this.startDate <= today;
    return isDealStartingInThePast ? defaultStartDate : this.startDate;
  }

  get status(): string {
    const today = DateTime.local().startOf('day');

    if (this.state === PackageStatus.Published) {
      if (today < this.startDate) {
        return i18n.global.t('states.upcoming');
      }
      if (this.startDate <= today && this.endDate >= today) {
        return i18n.global.t('states.live');
      }
      if (this.hasEnded) {
        return i18n.global.t('states.completed');
      }
      // Called in audit trails, without dates specified
      return i18n.global.t('states.published');
    }

    if (this.state === PackageStatus.Retired) {
      return i18n.global.t('states.retired');
    }

    return i18n.global.t('states.draft');
  }

  get targetingDescription(): string {
    const allNames = [
      ...(this.locations || []).map((x) => x.name),
      ...(this.sites || []).map((x) => x.name),
      ...(this.units || []).map((x) => x.name),
      ...(this.markets || []).map((x) => x.name),
      ...(this.geofenceGroups || []).map((x) => x.name),
    ];

    return allNames.length > 0 ? allNames.join(', ') : i18n.global.t('messages.allLocations');
  }

  get targetingDescriptionShort(): string {
    return this.targetingDescription.length > 30
      ? `${this.targetingDescription.slice(0, 30)} ...`
      : this.targetingDescription;
  }

  get targetParams(): IPackageTargetParams {
    // TODO: ask questions about some of these choices (min/max)
    return {
      ad_duration: this.adDurationMin ?? 0,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_formats: this.creativeFormats,
      creative_orientations: this.creativeOrientations,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      daypart: this.daypart,
      demographic_ids: this.demographicIDs,
      from_date: toServerDate(this.startDate),
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      lineitem_id: this.lineitemID,
      location_exclusion_ids: this.locationExclusionIDs,
      location_ids: this.locationIDs,
      market_ids: this.marketIDs,
      media_type_ids: this.mediaTypeIDs,
      network_ids: this.networkIDs,
      owner_ids: this.mediaOwnerIDs,
      package_id: this.id,
      purchase_type: this.purchaseType,
      screen_frequency_cap: this.minSecondsBetweenPlaysMin ?? 0,
      site_exclusion_ids: this.siteExclusionIDs,
      site_ids: this.siteIDs,
      to_date: toServerDate(this.endDate),
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unit_pack_exclusion_ids: this.unitPackExclusionIDs,
      unit_pack_ids: this.unitPackIDs,
      unit_tag_exclusions: this.unitTagExclusions,
      unit_tags: this.unitTags,
    };
  }

  get targetParamsEstimatedDeliveryStatistics(): IEstimatedDeliveryStatsAPI {
    const obj: IEstimatedDeliveryStatsAPI = {
      from_date: toServerDate(this.startDate),
      to_date: toServerDate(this.endDate),
      organization_id: this.organizationID,
      package_ids: this.packageIDs,
      unit_targeting: {
        package_id: this.id,
        lineitem_id: this.lineitemID,
        purchase_type: this.purchaseType,
        organization_id: this.organizationID,
        language_exclusion_ids: this.languageExclusionIDs || [],
        language_ids: this.languageIDs || [],
        location_ids: this.locationIDs || [],
        location_exclusion_ids: this.locationExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        market_ids: this.marketIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        network_ids: this.networkIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        owner_ids: this.isCuratedDeal ? this.ownerIDsFromChildDeals : this.mediaOwnerIDs,
        site_ids: this.siteIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        unit_ids: this.unitIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        creative_formats: this.creativeFormats,
        creative_format_exclusions: this.creativeFormatExclusions,
        creative_orientations: this.creativeOrientations,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        iab_categories: this.iabCategories,
        iab_category_exclusions: this.iabCategoryExclusions,
        unit_tags: this.unitTags,
        unit_tag_exclusions: this.unitTagExclusions,
        unitpack_ids: this.unitPackIDs,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        unique_advertiser_id_to_check: null,
      },
    };

    if (this.daypart != null) obj.daypart = this.daypart;
    if (this.demographicIDs != null) obj.demographic_ids = this.demographicIDs;
    if (this.minSecondsBetweenPlaysMin != null) {
      obj.screen_frequency_cap = this.minSecondsBetweenPlaysMin;
    }
    if (this.networkFrequencyCaps != null) obj.network_frequency_caps = this.networkFrequencyCaps.map((x) => x.toApi());
    if (this.maxBidCPM != null) obj.max_bid_cpm = this.maxBidCPM;
    if (!this.isCPMFromRateCard && isCuratorOrganization() && this.cpmFixed) obj.cpm_fixed = this.cpmFixed;

    return obj;
  }

  get unitCountFormatted(): string {
    return formatNumber(this.unitIDs.length, '0,0');
  }

  set locationIDs(val: number[]) {
    this.locationTargetIDs = val;
  }

  get targetingCriteria(): PackageTargetingCriterion[] {
    return this.packageTargetingCriteria || [];
  }

  get isTakeoverEnabled(): boolean {
    return false;
  }

  get totalImpressionsFormatted(): string {
    return this.deliveryStats?.totalImpressionsFormatted || DASH;
  }

  get averageCPMFormatted(): string {
    return this.deliveryStats?.averageCPM != null
      ? formatCurrency(this.deliveryStats.averageCPM, { currencyCode: this.currencyInformation.currency })
      : DASH;
  }

  get totalSpendFormatted(): string {
    return this.deliveryStats?.totalSpend != null
      ? formatCurrency(this.deliveryStats.totalSpend, { currencyCode: this.currencyInformation.currency })
      : DASH;
  }

  get totalCuratedDeliveryStatistics(): CuratedPackageDeliveryStatisticsEntry {
    return this.curatedDeliveryStatistics?.total;
  }

  get totalCuratedCPMFormatted(): string {
    return this.totalCuratedDeliveryStatistics?.eCPM != null
      ? formatCurrency(this.totalCuratedDeliveryStatistics.eCPM, { currencyCode: this.currencyInformation.currency })
      : DASH;
  }

  get hasAudienceTargeting(): boolean {
    // demographicsOverridable seems to be un-reliable since it can be set to false without having any demographics
    return this.packageProperties.some((x) => x.key === 'beta-audience') || this.demographicIDs.length > 0;
  }

  get targetCriteriaWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Weather,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Temperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaUV() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.UV,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allUVs().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationWeather,
    );

    if (weatherTargetingCriterion == null) return null;

    const weatherTargetingCriteria = this.targetingCriteria.filter(
      (tc) => tc.targetingCriterionID === weatherTargetingCriterion.id,
    );

    return weatherTargetingCriteria
      .map((tc) => {
        const weather = tc.values
          .filter((v) => v.code === 'WEATHER')
          .map(
            (v) =>
              v.value.split(',').map((v2) => {
                return WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name;
              }) || [],
          );
        const latLng = tc.values
          .filter((v) => v.code === 'LOCATION')
          .map((v) => v.value)
          .join(', ');

        return latLng
          ? `${weather.join(', ')} ${latLng} (${i18n.global.t('names.latitude')}, ${i18n.global.t('names.longitude')})`
          : weather.join(', ');
      })
      .flat();
  }

  get targetCriteriaDestinationTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationTemperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationRain() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationRain,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationSnow() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationSnow,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaPollen() {
    const pollenTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Pollen,
    );
    return pollenTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === pollenTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allPollens().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  //#region External SSP Properties

  get dealIDsBySspProperty() {
    return this.packageProperties.find((x) => x.key === LineItemPropertyKeys.DEAL_IDS_BY_SSP);
  }

  // Needed so that packages can support external SSPs
  get dealIDsByMediaOwnerProperty(): PackageProperty {
    return null;
  }

  //#endregion

  clone(): Package {
    // wars
    const cloned = Package.fromApi(this.toApi());
    cloned.id = -1;
    cloned.dealID = null;
    cloned.state = PackageStatus.Draft;
    cloned.timestamp = DateTime.local();
    cloned.packages = this.packages != null ? [...this.packages] : null;
    return cloned;
  }

  static createNew(
    pack = Package.fromObject({
      startDate: DateTime.local().startOf('day'),
      endDate: DateTime.local().startOf('day').plus({ weeks: 1 }).minus({ days: 1 }),
      numberOfDaysOverridable: true,
      adDurationOverridable: true,
      minSecondsBetweenPlaysOverridable: true,
      budgetOverridable: true,
      directBilling: false,
      marketTargetedImpressionsOverridable: true,
      demographicTargetsOverridable: true,
      locationTargetsOverridable: true,
      unitAssignmentsOverridable: true,
      networksOverridable: true,
      mediaTypesOverridable: true,
      daypartOverridable: true,
      purchaseType: PurchaseTypesEnum.GUARANTEED,
      budgetMode: BudgetModes.BUDGET,
      budgetMin: 0,
      budgetMax: 0,
      locationTargetIDs: [],
      isCPMFromRateCard: true,
      demographicTargetIDs: [],
      marketTargetedImpressionsMin: null,
      marketTargetedImpressionsMax: null,
      timestamp: DateTime.local(),
      cadenceMin: 0,
      cadenceMax: 0,
      cadenceSmoothCatchup: false,
    }),
  ): Package {
    return pack;
  }

  isPristine(): boolean {
    if (this.isNew) return true;

    let ignoreProperties = [
      'ad_duration_overridable',
      'archived',
      'available_from',
      'available_to',
      'budget_overridable',
      'buyer_access',
      'cadence_overridable',
      'created_by',
      'created_on_utc',
      'curator_access',
      'currency',
      'flight_from_max',
      'flight_from_min',
      'flight_to_max',
      'flight_to_min',
      'geofencegroups',
      'hide_unit_information_in_bid_request', // To be fixed.
      'inventory_targeting',
      'inventorytargeting_id', // Diffed individually.
      'location_exclusions',
      'locations',
      'market_targeted_impressions_overridable',
      'markets',
      'max_flight_to',
      'min_concentration_value', // Not supported by the BE
      'min_flight_from',
      'min_seconds_between_plays_overridable',
      'modified_by',
      'modified_on_utc',
      'networks',
      'package_children',
      'package_creatives',
      'package_locationtargets_overridable',
      'package_mediatypes_overridable',
      'package_networks_overridable',
      'package_targetingcriteria', // Diffed individually.
      'package_unitassignments_overridable',
      'revised_by_package_id',
      'seller_id',
      'sites',
      'units',
      'created_by',
      'modified_by',
      'package_targetingcriteria', // Diffed individually.
      'entity_fees', // Until the implementation is done on the BE
      'package_fee',
      'spend_steering',
    ];

    // A bunch of properties are not relevant for curated deals.
    if (this.isCuratedDeal) {
      ignoreProperties = [
        ...ignoreProperties,
        'ad_duration_max',
        'ad_duration_min',
        'budget_max',
        'budget_min',
        'budget_mode',
        'curator_organization_ids',
        'daypart',
        'daypart_overridable',
        'direct_billing',
        'geofencegroup_ids',
        'is_external',
        'location_exclusion_ids',
        'location_ids',
        'market_ids',
        'market_targeted_impressions_max',
        'market_targeted_impressions_min',
        'mediaowner_ids',
        'mediatype_ids',
        'min_concentration_factor',
        'min_seconds_between_plays_max',
        'min_seconds_between_plays_min',
        'network_ids',
        'number_of_days_overridable',
        'number_of_days_max',
        'number_of_days_min',
        'package_eventtargetingcriteria_overridable',
        'package_targetingcriteria_overridable',
        'plan_id',
        'purchase_type_overridable',
        'revision',
        'site_exclusion_ids',
        'site_ids',
        'unit_exclusion_ids',
        'unit_ids',
        'unit_tag_exclusions',
        'unit_tags',
        'cadence_max',
        'cadence_min',
        'cadence_smooth_catchup',
        'location_exclusion_ids',
        'location_exclusion_ids',
        'location_exclusion_ids',
        'location_exclusion_ids',
      ];
    }

    if (this.minSecondsBetweenPlaysOverridable && !this.isCuratedDeal) {
      // FE sets the value in int but when sending to BE it is setting it to null in toAPI()
      if (this.minSecondsBetweenPlaysMax === 0) ignoreProperties.push('min_seconds_between_plays_max');
      if (this.minSecondsBetweenPlaysMin === 0) ignoreProperties.push('min_seconds_between_plays_min');
    }

    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    const inventoryTargetingDiffs = getDiff(this.apiObject.inventory_targeting, this.toApi().inventory_targeting, [
      'unique_advertiser_id_to_check',
      'co2e_emissions',
      'inventorytargeting_id',
    ]);
    const packageTargetCriteriaDiffs = getDiff(
      this.apiObject.package_targetingcriteria,
      this.toApi().package_targetingcriteria,
      ['packagetargetingcriterion_id'],
    );
    if (diffs.length === 0 && inventoryTargetingDiffs.length === 0 && packageTargetCriteriaDiffs.length === 0)
      return true;
    DatadogApi.logUnexpectedChange([...diffs, ...inventoryTargetingDiffs, ...packageTargetCriteriaDiffs]);
    return false;
  }

  static fromApi(apiObject: IPackageAPI): Package {
    const packageProperties = apiObject.package_properties?.map((x) => PackageProperty.fromApi(x)) || [];
    const isExistingCuratedPackage = apiObject.created_on_utc && apiObject.package_type === PackageType.CURATED;
    const defaultEntityFees = [
      EntityFee.fromObject({
        value: apiObject.package_fee?.value ?? 0,
        feeType: apiObject.package_fee?.type ?? PackageFeeType.FIXED,
      }),
    ];

    let entityFees = [];
    if (isExistingCuratedPackage) {
      // If entity fees are present, we should use them, otherwise we should rely on the original package fee
      if (apiObject.entity_fees?.length) {
        entityFees = apiObject.entity_fees.map((x) => EntityFee.fromApi(x));
      } else if (apiObject.package_fee) {
        entityFees = defaultEntityFees;
      }
    } else {
      entityFees = defaultEntityFees;
    }

    const pack = Package.fromObject({
      adDurationMax: apiObject.ad_duration_max,
      adDurationMin: apiObject.ad_duration_min,
      adDurationOverridable: apiObject.ad_duration_overridable || false,
      auctionType: apiObject.auction_type,
      budgetMode: apiObject.budget_mode,
      budgetMax: apiObject.budget_max,
      budgetMin: apiObject.budget_min,
      budgetOverridable: apiObject.budget_overridable || false,
      buyerExternalExchangeIDs: apiObject.buyer_external_exchange_ids,
      buyerOrganizationDSPIDs: apiObject.buyer_organization_dsp_ids,
      curatorOrganizationIDs: apiObject.curator_organization_ids,
      co2Emissions: {
        adSelectionGco2e: apiObject.inventory_targeting?.co2e_emissions?.ad_selection_gco2e,
        creativeDeliveryGco2e: apiObject.inventory_targeting?.co2e_emissions?.creative_delivery_gco2e,
        inventoryTargetingID: apiObject.inventory_targeting?.co2e_emissions?.inventorytargeting_id,
        mediaDistributionGco2e: apiObject.inventory_targeting?.co2e_emissions?.media_distribution_gco2e,
        score: apiObject.inventory_targeting?.co2e_emissions?.score,
        totalGco2e: apiObject.inventory_targeting?.co2e_emissions?.total_gco2e,
      },
      co2EmissionsScore: apiObject.inventory_targeting?.co2e_emissions?.score,
      co2EmissionsScoreThreshold: apiObject.inventory_targeting?.co2e_emissions_score_threshold,
      cpmFixed: apiObject.cpm_fixed,
      cpmNet: apiObject.cpm_net,
      createdBy: apiObject.created_by,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      customEventTargetingOverridable: apiObject.package_eventtargetingcriteria_overridable || false,
      creativeOrientations: apiObject.inventory_targeting?.creative_orientations || [],
      creativeOrientationExclusions: apiObject.inventory_targeting?.creative_orientation_exclusions || [],
      iabCategories: apiObject.inventory_targeting?.iab_categories || [],
      iabCategoryExclusions: apiObject.inventory_targeting?.iab_category_exclusions || [],
      daypart: apiObject.daypart,
      daypartOverridable: apiObject.daypart_overridable || false,
      dealID: apiObject.deal_id,
      demographicTargetIDs: apiObject.demographic_ids || [],
      demographicTargetsOverridable: apiObject.package_demographictargets_overridable || false,
      directBilling: apiObject.direct_billing,
      endDate: DateTime.fromISO(apiObject.flight_to),
      exchangeID: apiObject.exchange_id,
      entityFees,
      isEvergreen: apiObject.is_evergreen,
      languageIDs: apiObject.inventory_targeting?.language_ids || [],
      languageExclusionIDs: apiObject.inventory_targeting?.language_exclusion_ids || [],
      locationIDs: apiObject.inventory_targeting?.location_ids || [],
      locationExclusionIDs: apiObject.inventory_targeting?.location_exclusion_ids || [],
      geofenceGroupIDs: apiObject.inventory_targeting?.geofencegroup_ids || [],
      geofenceGroupExclusionIDs: apiObject.inventory_targeting?.geofencegroup_exclusion_ids || [],
      marketIDs: apiObject.inventory_targeting?.market_ids || [],
      marketExclusionIDs: apiObject.inventory_targeting?.market_exclusion_ids || [],
      mediaTypeIDs: apiObject.inventory_targeting?.mediatype_ids,
      mediaTypeExclusionIDs: apiObject.inventory_targeting?.mediatype_exclusion_ids || [],
      networkIDs: apiObject.inventory_targeting?.network_ids || [],
      networkExclusionIDs: apiObject.inventory_targeting?.network_exclusion_ids || [],
      siteIDs: apiObject.inventory_targeting?.site_ids || [],
      siteExclusionIDs: apiObject.inventory_targeting?.site_exclusion_ids || [],
      unitIDs: apiObject.inventory_targeting?.unit_ids || [],
      unitExclusionIDs: apiObject.inventory_targeting?.unit_exclusion_ids || [],
      unitPackIDs: apiObject.inventory_targeting?.unitpack_ids || [],
      unitPackExclusionIDs: apiObject.inventory_targeting?.unitpack_exclusion_ids || [],
      mediaOwnerIDs: apiObject.inventory_targeting?.owner_ids,
      geofenceGroups: apiObject.geofencegroups?.map((x) => GeofenceGroup.fromApi(x)) || [],
      hideUnitInformationInBidRequests:
        apiObject.hide_unit_information_in_bid_request != null ? apiObject.hide_unit_information_in_bid_request : false,
      id: apiObject.package_id,
      isCPMFromRateCard: apiObject.cpm_from_rate_card,
      isExternal: apiObject.is_external,
      isForBidding: apiObject.for_bidding,
      isOpen: apiObject.is_open,
      locationExclusions: apiObject.location_exclusions?.map((x) => Location.fromApi(x)) || [],
      locations: apiObject.locations?.map((x) => Location.fromApi(x)) || [],
      locationTargetIDs: apiObject.inventory_targeting?.location_ids,
      locationTargetsOverridable: apiObject.package_locationtargets_overridable || false,
      markets: apiObject.markets ? apiObject.markets.map((x) => Market.fromApi(x)) : [],
      marketTargetedImpressionsMax: apiObject.market_targeted_impressions_max,
      marketTargetedImpressionsMin: apiObject.market_targeted_impressions_min,
      marketTargetedImpressionsOverridable: apiObject.market_targeted_impressions_overridable || false,
      minConcentrationFactor: apiObject.min_concentration_factor == null ? 1 : apiObject.min_concentration_factor,
      minConcentrationValue: apiObject.min_concentration_value || 0,
      minSecondsBetweenPlaysMax: apiObject.min_seconds_between_plays_max,
      minSecondsBetweenPlaysMin: apiObject.min_seconds_between_plays_min,
      minSecondsBetweenPlaysOverridable: apiObject.min_seconds_between_plays_overridable || false,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      name: apiObject.name,
      networks: apiObject.networks?.map((x) => Network.fromApi(x)) || [],
      networksOverridable: apiObject.package_networks_overridable || false,
      mediaTypes: apiObject.mediatypes?.map((x) => MediaType.fromApi(x)) || [],
      mediaTypesOverridable: apiObject.package_mediatypes_overridable || false,
      notes: apiObject.notes,
      numberOfDaysMax: apiObject.number_of_days_max,
      numberOfDaysMin: apiObject.number_of_days_min,
      numberOfDaysOverridable: apiObject.number_of_days_overridable || false,
      organizationID: apiObject.organization_id,
      packageIDs: apiObject.package_ids,
      packageProperties,
      packageTargetingCriteria:
        apiObject.package_targetingcriteria != null
          ? apiObject.package_targetingcriteria.map((x) => PackageTargetingCriterion.fromApi(x))
          : [],
      packageTargetingCriteriaOverridable: apiObject.package_targetingcriteria_overridable || false,
      purchaseType: apiObject.purchase_type,
      purchaseTypeOverridable: apiObject.purchase_type_overridable || false,
      revisedByID: apiObject.revised_by_package_id,
      revision: apiObject.revision,
      siteExclusions: apiObject.site_exclusions != null ? apiObject.site_exclusions.map((x) => Site.fromApi(x)) : [],
      sites: apiObject.sites != null ? apiObject.sites.map((x) => Site.fromApi(x)) : [],
      startDate: DateTime.fromISO(apiObject.flight_from),
      state: apiObject.state,
      unitAssignmentsOverridable: apiObject.package_unitassignments_overridable || false,
      unitExclusions: apiObject.unit_exclusions != null ? apiObject.unit_exclusions.map((x) => Unit.fromApi(x)) : [],
      units: apiObject.units != null ? apiObject.units.map((x) => Unit.fromApi(x)) : [],
      unitTagExclusions: apiObject.inventory_targeting?.unit_tag_exclusions,
      unitTags: apiObject.inventory_targeting?.unit_tags,
      cadenceMin: apiObject.cadence_min,
      cadenceMax: apiObject.cadence_max,
      cadenceSmoothCatchup: apiObject.cadence_smooth_catchup,
      creativeFormats: apiObject.inventory_targeting?.creative_formats || [],
      creativeFormatExclusions: apiObject.inventory_targeting?.creative_format_exclusions || [],
      planID: apiObject.plan_id,
      packageType: apiObject.package_type,
      packages: apiObject.package_children?.map((x) => Package.fromApi(x)) || null,
      curationType: apiObject.curation_type,
      curatedDeliveryStatistics:
        apiObject.estimated_delivery_statistics != null
          ? CuratedPackageDeliveryStatistics.fromApi(apiObject?.estimated_delivery_statistics)
          : null,
      spendSteering: apiObject.spend_steering ? SpendSteering.fromApi(apiObject.spend_steering) : null,
      currency: apiObject.currency,
    });
    pack.loadCustomProperties();
    pack.apiObject = apiObject;

    return pack;
  }

  static fromObject(obj): Package {
    return super.fromObject(obj, new Package());
  }

  getExtraValidation() {
    const extras: Record<string, IExtraValidation> = {};

    switch (this.feeType) {
      case PackageFeeType.FIXED:
        extras.feeValue = {
          value: this.feeValue,
          rules: 'required|numeric:float|min_value:0',
        };
        break;
      case PackageFeeType.PERCENT:
        extras.feeValue = {
          value: this.feeValue,
          rules: 'required|numeric:float|min_value:0',
        };
        break;
      default:
        break;
    }

    if (!this.isCPMFromRateCard) {
      extras.cpmFixed = {
        value: this.cpmFixed,
        rules: 'required|numeric:float|min_value:0',
      };
    }

    // Spend steering validation
    if (this.spendSteering) {
      const availableDimensions = [
        ...new Set(
          this.spendSteering.dimensionType === SelectableDimensionType.DEAL
            ? this.packages.map((x) => x.dealID)
            : this.packages.map((x) => String(x.organizationID)),
        ),
      ];
      const activeSteeringDimensions = this.spendSteering.spendSteeringDimensions.filter(
        (x) => x.dimensionValue !== '*',
      );
      const areAllDimensionsSelected = availableDimensions.length === activeSteeringDimensions.length;
      if (this.spendSteering.spendSteeringDimensions.length === 0) {
        extras.dealSpendSteeringAtLeastOneDimensionMustBeSelected = {
          message: capitalize(i18n.global.t('messages.dealSpendSteeringAtLeastOneDimensionMustBeSelected')),
          preEvaluated: true,
        };
      }
      if (activeSteeringDimensions.some((x) => x.steeringPercentage < MINIMUM_STEERING_PERCENTAGE)) {
        extras.spendSteeringAllSteeringPercentagesMustBeGreaterThanZero = {
          message: capitalize(i18n.global.t('messages.spendSteeringAllSteeringPercentagesMustBeGreaterThanZero')),
          preEvaluated: true,
        };
      }
      if (areAllDimensionsSelected && this.spendSteering.remainingSteeringPercentage > 0) {
        extras.dealSpendSteeringTotalPercentage = {
          message: capitalize(i18n.global.t('messages.dealSpendSteeringTotalPercentage')),
          preEvaluated: true,
        };
      }
      if (
        availableDimensions.length > 0 &&
        !areAllDimensionsSelected &&
        this.spendSteering.remainingSteeringPercentage === 0
      ) {
        extras.dealSpendSteeringActiveWildCardTotalPercentage = {
          message: capitalize(i18n.global.t('messages.dealSpendSteeringActiveWildCardTotalPercentage')),
          preEvaluated: true,
        };
      }
    }

    if (this.hasMismatchedPublisherDeals) {
      extras.mismatchedPublisherDeals = {
        preEvaluated: true,
        message: capitalize(i18n.global.t('messages.dealHasIncompatiblePDs')),
      };
    }

    if (this.hasIncompatibleRestrictions) {
      extras.incompatibleRestrictions = {
        preEvaluated: true,
        message: capitalize(i18n.global.t('messages.dealHasIncompatibleRestrictions')),
      };
    }

    // If no publisher deals are selected or the curation type is not towards eSSPs
    if (this.isCuratedDeal && !this.packageIDs?.length && this.curationType !== CurationType.EXTERNAL_SSP_DEALS) {
      extras.curatedDealMustHavePublisherDeals = {
        preEvaluated: true,
        message: capitalizeTranslation('messages.curatedDealMissingPublisherDeals'),
      };
    }

    return extras;
  }

  loadCustomProperties(): PackageCustomProperty[] {
    const orgSettings = rootState.orgApp.organizationSettings;
    if (orgSettings == null || orgSettings.packageCustomProperties == null) {
      return;
    }

    this.packageCustomProperties = orgSettings.packageCustomProperties.map((x) => {
      const existing = this.packageProperties.find((pp) => pp.key === x.key);
      const clonedProp = PackageCustomProperty.fromApi(x.toApi());
      if (existing) {
        if (x.type === 'search') {
          const existingName = this.packageProperties.find((pp) => pp.key === `${x.key}-name`);
          if (existingName != null) {
            clonedProp.value = {
              id: existing.value,
              name: existingName.value,
            };
          }
        } else {
          clonedProp.value = existing.value;
        }
      }

      return clonedProp;
    });
  }

  toApi() {
    // Handles special cadence budget mode 'PLAYS PER HOUR'.
    const budgetMode = this.budgetMode !== null ? this.budgetMode : BudgetModes.IMPRESSIONS;

    const packageProperties = [...this.packageProperties];

    // NEVER put a value for budget_min/budget_max if the budget mode is impressions.
    // Same thing if the budget mode is budget, never put anything for
    // the market target imps min/max.
    if (budgetMode === BudgetModes.BUDGET) {
      this.marketTargetedImpressionsMin = null;
      this.marketTargetedImpressionsMax = null;
      if (this.isPreferredOrNGInternalDeal) {
        this.budgetMin = null;
        this.budgetMax = null;
      }
    } else if (budgetMode === BudgetModes.IMPRESSIONS) {
      this.budgetMin = null;
      this.budgetMax = null;
      if (this.isPreferredOrNGInternalDeal) {
        this.marketTargetedImpressionsMin = null;
        this.marketTargetedImpressionsMax = null;
      }
    }

    const apiObject: IPackageAPI = {
      ad_duration_max: this.adDurationOverridable ? null : this.adDurationMax,
      ad_duration_min: this.adDurationOverridable ? null : this.adDurationMin,
      ad_duration_overridable: this.adDurationOverridable,
      budget_max: this.budgetMax,
      budget_min: this.budgetMin,
      budget_mode: budgetMode,
      budget_overridable: this.budgetOverridable,
      buyer_external_exchange_ids: this.buyerExternalExchangeIDs,
      buyer_external_ids: this.buyerExternalIDs,
      buyer_organization_dsp_ids: this.buyerOrganizationDSPIDs,
      cpm_fixed: this.isCPMFromRateCard ? null : this.cpmFixed,
      cpm_from_rate_card: this.isCPMFromRateCard,
      created_by: this.createdBy,
      creative_format_exclusions: this.creativeFormatExclusions,
      creative_formats: this.creativeFormats,
      curation_type: this.curationType,
      curator_organization_ids: this.curatorOrganizationIDs,
      daypart: this.daypart,
      daypart_overridable: this.daypartOverridable,
      deal_id: this.dealID != null && this.id > -1 ? this.dealID : undefined,
      demographic_ids: this.demographicTargetIDs,
      direct_billing: this.directBilling,
      exchange_id: this.exchangeID,
      flight_from: toServerDate(this.startDate),
      flight_to: toServerDate(this.endDate),
      for_bidding: this.isForBidding,
      geofencegroup_ids: this.geofenceGroupIDs,
      hide_unit_information_in_bid_request: this.hideUnitInformationInBidRequests,
      is_evergreen: this.isNGOrPreferredDeal ? this.isEvergreen : false,
      is_external: this.isExternal,
      is_open: false,
      location_exclusion_ids: this.locationExclusionIDs,
      location_ids: this.locationTargetIDs,
      market_ids: this.marketIDs,
      market_targeted_impressions_max: this.marketTargetedImpressionsMax,
      market_targeted_impressions_min: this.marketTargetedImpressionsMin,
      market_targeted_impressions_overridable: this.marketTargetedImpressionsOverridable,
      mediaowner_ids: this.mediaOwnerIDs,
      mediatype_ids: this.mediaTypeIDs,
      min_concentration_factor: this.minConcentrationFactor,
      min_concentration_value: this.minConcentrationValue,
      min_seconds_between_plays_max: this.minSecondsBetweenPlaysOverridable ? null : this.minSecondsBetweenPlaysMax,
      min_seconds_between_plays_min: this.minSecondsBetweenPlaysOverridable ? null : this.minSecondsBetweenPlaysMin,
      min_seconds_between_plays_overridable: this.minSecondsBetweenPlaysOverridable,
      name: this.name,
      network_ids: this.networkIDs,
      notes: this.notes,
      number_of_days_overridable: true, // this.numberOfDaysOverridable, // disabled for now
      number_of_days_max: this.numberOfDaysMax,
      number_of_days_min: this.numberOfDaysMin,
      organization_id: this.organizationID,
      package_demographictargets_overridable: this.demographicTargetsOverridable,
      package_eventtargetingcriteria_overridable: this.customEventTargetingOverridable,
      package_fee: this.feeValue != null && this.feeType != null ? { type: this.feeType, value: this.feeValue } : null,
      entity_fees: this.feeAttachableCategoryID ? this.entityFees.map((e) => e.toApi()) : [],
      package_id: this.id >= 0 ? this.id : undefined,
      package_ids: this.packageIDs,
      package_locationtargets_overridable: this.locationTargetsOverridable,
      package_mediatypes_overridable: this.mediaTypesOverridable,
      package_networks_overridable: this.networksOverridable,
      package_properties: packageProperties.map((x) => x.toApi()),
      package_targetingcriteria: this.packageTargetingCriteria?.map((x) => x.toApi()) || undefined,
      package_targetingcriteria_overridable: this.packageTargetingCriteriaOverridable,
      package_type: this.packageType,
      package_unitassignments_overridable: this.unitAssignmentsOverridable,
      plan_id: this.planID,
      purchase_type: this.purchaseType,
      purchase_type_overridable: this.purchaseTypeOverridable,
      revision: this.revision,
      site_exclusion_ids: this.siteExclusionIDs,
      site_ids: this.siteIDs,
      sites: this.sites.map((x) => x.toApi()),
      spend_steering: this.spendSteering ? this.spendSteering.toApi(this.packages) : null,
      state: this.state,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unit_tag_exclusions: this.unitTagExclusions,
      unit_tags: this.unitTags,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      unitpack_ids: this.unitPackIDs,
      units: this.units.map((x) => x.toApi()),
      inventory_targeting: {
        co2e_emissions_score_threshold: this.co2EmissionsScoreThreshold,
        creative_formats: this.creativeFormats,
        creative_format_exclusions: this.creativeFormatExclusions,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        creative_orientations: this.creativeOrientations,
        iab_categories: this.iabCategories,
        iab_category_exclusions: this.iabCategoryExclusions,
        market_ids: this.marketIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        owner_ids: this.mediaOwnerIDs,
        language_exclusion_ids: this.languageExclusionIDs,
        language_ids: this.languageIDs,
        location_ids: this.locationIDs,
        location_exclusion_ids: this.locationExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        network_ids: this.networkIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        site_ids: this.siteIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        unique_advertiser_id_to_check: null,
        unit_ids: this.unitIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        unit_tags: this.unitTags,
        unit_tag_exclusions: this.unitTagExclusions,
        unitpack_ids: this.unitPackIDs,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
      },
    };

    if (this.isPreferredOrNGInternalDeal) {
      apiObject.ad_duration_min = this.adDurationMin;
      apiObject.ad_duration_max = this.adDurationMax;
      apiObject.min_seconds_between_plays_min = this.minSecondsBetweenPlaysMin;
      apiObject.min_seconds_between_plays_max = this.minSecondsBetweenPlaysMax;
    }

    if (this.purchaseType === PurchaseTypesEnum.CADENCE) {
      apiObject.cadence_max = this.cadenceMax;
      apiObject.cadence_min = this.cadenceMin;
      apiObject.cadence_smooth_catchup = this.cadenceSmoothCatchup;
    } else {
      apiObject.cadence_max = null;
      apiObject.cadence_min = null;
      apiObject.cadence_smooth_catchup = false;
    }

    // Eventually look into if we should add restrictions to the external flag.
    if (this.isNGExternalOrPreferredExternalDeal || this.curatorOrganizationIDs?.length > 0) {
      apiObject.ad_duration_min = this.adDurationMin;
      apiObject.ad_duration_max = this.adDurationMax;
      apiObject.auction_type = this.auctionType;
      apiObject.is_open = this.isOpen;
    } else if (this.apiObject) {
      apiObject.auction_type = this.apiObject.auction_type;
    }

    // Safety check to ensure that if we are restricting the package to specific curators that for_bidding is null/none.
    if (this.curatorOrganizationIDs?.length > 0) {
      apiObject.for_bidding = null;
    }

    if (this.isInlineEdited) {
      Package.expandedProperties.forEach((property) => delete apiObject[property]);
    }

    if (apiObject.package_fee == null && !this.isCuratedDeal) delete apiObject.package_fee;
    if (apiObject.entity_fees == null) delete apiObject.entity_fees;
    if (apiObject.package_ids == null) delete apiObject.package_ids;
    if (apiObject.buyer_external_ids == null || apiObject.buyer_external_ids.length === 0)
      delete apiObject.buyer_external_ids;

    return apiObject;
  }

  toApiStateChange(): { state: PackageStatus; approval_comment: string } {
    return {
      state: this.state,
      approval_comment: this.approvalComment, // ! ????
    };
  }

  static validatorCreateRules() {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      state: 'required|max:1',
      startDate: 'required',
      numberOfDaysMin: 'numeric:int',
      numberOfDaysMax: 'numeric:int',
      numberOfDaysOverridable: 'required|boolean',
      adDurationOverridable: 'required|boolean',
      minSecondsBetweenPlaysOverridable: 'required|boolean',
      budgetOverridable: 'required|boolean',
      isCPMFromRateCard: 'required|boolean',
      marketTargetedImpressionsOverridable: 'required|boolean',
      demographicTargetsOverridable: 'required|boolean',
      locationTargetsOverridable: 'required|boolean',
      unitAssignmentsOverridable: 'required|boolean',
      networksOverridable: 'required|boolean',
      mediaTypesOverridable: 'required|boolean',
      purchaseType: 'required',
      purchaseTypeOverridable: 'required|boolean',
      minConcentrationFactor: 'required|numeric:float|min_value: 0',
      minConcentrationValue: 'required|numeric:float|min_value: 0',
    };
  }
}
